import React, { useEffect, useRef, useState } from 'react';
import { Keyboard, Platform, Pressable, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import { SafeAreaView } from 'react-native-safe-area-context';
import {
  GEARS,
  GEAR_META,
  Gear,
  Sector,
  Stage,
  TOOL_NAMES,
} from '../domain/olmps';
import { GEAR_TIERS, TIER_ORDER, GearId } from './ascension';
import { SECTION_TOOLS } from './orderSections';
import { useTheme, makeThemedStyles, radii } from '../v10/theme';
import { CloudBackdrop } from './primitives';
import { Atmosphere, GlassFill } from './glass';
import { useAuth } from '../auth/AuthProvider';
import { readNav, readNavSync, writeNav } from '../lib/navPersist';
import { V10Home } from '../v10/V10Home';
import { useCompassConfig } from './sectorConfig';
import { useTodayData } from './useTodayData';
import { TodayDataProvider } from './TodayDataContext';
import { OlmpsHeader } from './OlmpsHeader';
import { NowStrip } from './NowStrip';
import { StepScreen, StepItem } from './StepScreen';
import { SessionFlow } from './SessionFlow';
import { SpineFlow } from './SpineFlow';
import { CompassView } from './CompassView';
import { SyqiHome } from './SyqiHome';
import { NavDrawer } from './NavDrawer';
import { ToolPanel } from './ToolPanel';
import { onOpenScreen } from '../live/appActions';
import { setScreen } from '../live/screenContext';
import { getDeityAvatar } from './deityAvatars';
import { InfoPopup } from './InfoPopup';
import { SyqiChat } from './SyqiChat';
import { HrmezPopup } from './HrmezPopup';
import { toggleTalk } from '../v10/TalkingOverlay';
import { SettingsSheet } from './SettingsSheet';
import { getRevealEnabled } from './revealPref';
import { SpectrumScreen } from './SpectrumScreen';
import { SpectrumReveal } from './SpectrumReveal';
import { runWeeklyTally } from './spectrum-data';
import { isWeekBoundaryEvening } from './gameSettings';
import { supabase } from '../lib/supabase';
import { GearTile } from './primitives';

// ─────────────────────────────────────────────────────────────────────────
// OLMPS v9 shell — the daily loop. Home = SyQi, a widget home (SyqiHome)
// hosting the always-on Today table plus a configurable set of summary
// widgets (see ./widgets.ts). The orbital compass is now its own tool,
// ASTRA (view 'astra' → CompassView). The DMT spectrum ("place your line")
// is MoyRi (view 'spectrum' → SpectrumScreen). Bottom nav = SyQi + the
// user's configured tool icons + Settings.
// Views: oracle (home, display "SyQi") | astra | tool | step | session | spectrum(=MoyRi).
// ─────────────────────────────────────────────────────────────────────────

// 'oracle' view = SyQi home (display renamed V9)
type ShellView = 'now' | 'oracle' | 'astra' | 'tool' | 'step' | 'session' | 'spectrum' | 'spine';
// Views that can be restored after a refresh. 'step'/'session' need in-memory
// objects (the StepItem, session kind flow), so those fall back to home.
type SavedShell = { view: ShellView; toolId: string | null; gear: Gear };
const RESTORABLE: ShellView[] = ['now', 'oracle', 'astra', 'tool', 'spectrum', 'spine'];
function validShell(s: SavedShell | null): SavedShell | null {
  if (!s || !RESTORABLE.includes(s.view)) return null;
  if (s.view === 'tool' && !s.toolId) return null;
  return s;
}
type Overlay = 'none' | 'info' | 'chat' | 'hrmez';
// Landscape collapse overlays (tool view only): clock reveals the ORDER
// gears, compass reveals the shell nav row.
type LandOverlay = 'none' | 'order';

// Sector/stage are no longer navigable state — ToolPanel/ToolHost still take
// them as plumbing (every native tool ignores the values), so fixed
// constants are passed through.
const SHELL_SECTOR: Sector = 'H';
const SHELL_STAGE: Stage = 'T';

// Best-effort gear guess for a tool opened outside a known row/step context
// (e.g. via the "All Tools" menu) — used only to give InfoPopup a sensible
// question to show. Built from ascension.ts's GEAR_TIERS (the single shared
// 15-DMT roster) FIRST, so anchor/tiered tools (telos, kyrs, moyri, metis,
// clio, etc.) resolve to their true gear instead of defaulting to 'orient'.
// Non-DMT widget ids (e.g. jnus — the widget id for the janus DMT — akeso,
// sofia, astrs, epic…) fall back to their SECTION_TOOLS gear.
const TOOL_DEFAULT_GEAR: Partial<Record<string, Gear>> = {};
for (const gear of Object.keys(GEAR_TIERS) as GearId[]) {
  for (const tier of TIER_ORDER) {
    TOOL_DEFAULT_GEAR[GEAR_TIERS[gear][tier]] = gear;
  }
}
for (const gear of GEARS) {
  for (const toolId of SECTION_TOOLS[gear]) {
    if (!(toolId in TOOL_DEFAULT_GEAR)) TOOL_DEFAULT_GEAR[toolId] = gear;
  }
}
function guessGearForTool(toolId: string): Gear {
  return TOOL_DEFAULT_GEAR[toolId] ?? 'orient';
}

/** True when the window is wider than tall, except while the user is typing: a phone keyboard
 *  shrinks the window until it is wider than tall, which is not a rotation, and swapping to the
 *  landscape layout would remount the open tool and wipe what is being typed. */
function useStableLandscape(): boolean {
  const { width, height } = useWindowDimensions();
  const raw = width > height;
  const last = useRef(raw);
  let typing = false;
  if (Platform.OS === 'web') {
    const el = typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null;
    typing = !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
  } else {
    typing = Keyboard.isVisible();
  }
  if (!typing) last.current = raw;
  return last.current;
}

export function OlmpsCore() {
  const styles = useStyles();
  const { session } = useAuth();
  const userId = session?.user.id;
  const { config: compassConfig, loading: configLoading, markDay } = useCompassConfig(userId);
  const today = useTodayData(userId, { ledger: compassConfig.ledger, game: compassConfig.game });
  const isLandscape = useStableLandscape();

  // Restored from the last session so a refresh stays on the same screen.
  const [savedShell] = useState(() => validShell(readNavSync<SavedShell>('shell')));
  const [view, setView] = useState<ShellView>(savedShell?.view ?? 'now');
  const [toolId, setToolId] = useState<string | null>(savedShell?.toolId ?? null);
  const [toolProps, setToolProps] = useState<
    { initialProtocolId?: string; initialTaskId?: string } | undefined
  >(undefined);
  const [activeGear, setActiveGear] = useState<Gear>(savedShell?.gear ?? 'orient');

  // Native has no sync storage — restore once after mount instead.
  useEffect(() => {
    if (Platform.OS === 'web') return;
    void readNav<SavedShell>('shell').then((s) => {
      const v = validShell(s);
      if (!v) return;
      setToolId(v.toolId);
      setActiveGear(v.gear);
      setView(v.view);
    });
  }, []);

  useEffect(() => {
    writeNav('shell', RESTORABLE.includes(view) ? { view, toolId: view === 'tool' ? toolId : null, gear: activeGear } : null);
  }, [view, toolId, activeGear]);
  const [stepItem, setStepItem] = useState<StepItem | null>(null);
  const [sessionKind, setSessionKind] = useState<'morning' | 'night'>('morning');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsPending = useRef(false);
  // Bumped when Settings closes so the Compass remounts and re-reads its
  // ORACLE spin settings (they're loaded once per mount).
  const [settingsRev, setSettingsRev] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [overlay, setOverlay] = useState<Overlay>('none');
  const [landOverlay, setLandOverlay] = useState<LandOverlay>('none');
  const [revealOpen, setRevealOpen] = useState(false);

  // Catch-up on open: tally any completed-but-unrevealed weeks (so data never
  // stalls when a week close was missed) and, if the user wants it, show the
  // reveal for them. The live close runs in the boundary-evening session
  // (SessionFlow), so on the boundary evening itself the overlay stays out of
  // the way. Idempotent; never blocks the shell. Waits for the user's settings
  // so the week boundary is theirs.
  const game = compassConfig.game;
  useEffect(() => {
    if (!userId || configLoading) return;
    let alive = true;
    (async () => {
      try {
        await runWeeklyTally(supabase, userId, new Date(), game);
      } catch {
        /* best-effort */
      }
      if (alive && !isWeekBoundaryEvening(new Date(), game) && (await getRevealEnabled(userId))) setRevealOpen(true);
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, configLoading]);

  // Pinch-to-zoom for the landscape tool view (shrink to fit or magnify).
  const [zoom, setZoom] = useState(1);
  const zoomBase = useRef(1);
  const pinch = Gesture.Pinch()
    .onUpdate((e) => {
      const next = Math.min(2.5, Math.max(0.5, zoomBase.current * e.scale));
      setZoom(next);
    })
    .onEnd(() => {
      zoomBase.current = Math.min(2.5, Math.max(0.5, zoom));
    });

  // Drawer / header Home → lands on the dashboard of the v10 canvas.
  const [homeTick, setHomeTick] = useState(0);
  const goHome = () => {
    closeToToday();
    setHomeTick((n) => n + 1);
  };
  const closeToToday = () => {
    setView('now');
    setToolId(null);
    setToolProps(undefined);
    setStepItem(null);
  };

  const openOracle = () => setView('oracle');
  const openAstra = () => setView('astra');
  const openSpectrum = () => setView('spectrum');
  const openSpine = () => setView('spine');

  // Single entry point for opening any tool, from any surface (bottom nav,
  // drawer, All Tools, orb links, sessions). ASTRA always means the orbital
  // compass (not the projects/charts hub) and MoyRi always means the DMT
  // spectrum screen, so those two redirect to their dedicated views; every
  // other tool opens in the tool panel.
  const openTool = (
    id: string,
    gear?: Gear,
    opts?: { initialProtocolId?: string; initialTaskId?: string }
  ) => {
    if (id === 'astrs') return setView('astra'); // ledger #13: spine editor opens ASTRA orbital view
    if (id === 'moyri') return setView('spectrum');
    // ORACLE is renamed ATLAS; TELOS folds into it.
    if (id === 'oracle' || id === 'telos') id = 'atlas';
    setToolId(id);
    setActiveGear(gear ?? guessGearForTool(id));
    setToolProps(opts);
    setView('tool');
  };

  const openStep = (item: StepItem) => {
    setStepItem(item);
    setView('step');
  };

  const openSession = (kind: 'morning' | 'night') => {
    setSessionKind(kind);
    setView('session');
  };

  const routeGear = (gear: Gear) => {
    setLandOverlay('none');
    switch (gear) {
      case 'orient':
        setView('astra');
        break;
      case 'regulate':
        openTool('numa', 'regulate');
        break;
      case 'direct':
        openTool('kyrs', 'direct');
        break;
      case 'execute': {
        const item = today.currentItem ?? today.nextItem;
        if (item) {
          openStep({
            id: item.itemId,
            rowId: item.rowId,
            text: item.text,
            time: item.time || undefined,
            durationMinutes: item.durationMinutes,
          });
        } else {
          openTool('metis', 'execute');
        }
        break;
      }
      case 'revise':
        openTool('clio', 'revise');
        break;
    }
  };

  const resetZoom = () => {
    zoomBase.current = 1;
    setZoom(1);
  };

  const title =
    view === 'tool' && toolId
      ? TOOL_NAMES[toolId] ?? toolId
      : view === 'oracle'
      ? 'SyQi'
      : view === 'astra'
      ? 'ASTRA'
      : view === 'spectrum'
      ? 'MoyRi'
      : view === 'step'
      ? 'STEP'
      : view === 'session'
      ? sessionKind === 'morning'
        ? 'MORNING SESSION'
        : 'EVENING SESSION'
      : view === 'spine'
      ? 'SETUP'
      : 'OLMPS';

  // Screen awareness for the talking deities. The v10 home ('now') reports its own canvas position.
  useEffect(() => {
    if (view === 'now') return;
    setScreen({
      screen: view === 'tool' ? `Tool: ${title}` : title,
      toolId: view === 'tool' && toolId ? toolId : undefined,
      toolName: view === 'tool' && toolId ? title : undefined,
      deityName: view === 'tool' && toolId ? getDeityAvatar(toolId)?.deityName : undefined,
      gear: activeGear,
      domId: 'olmps-shell-content',
    });
  }, [view, toolId, title, activeGear]);

  // Voice agents open tools / screens ("SyQi, open KYRS"). TalkHost lives at the app root (App.tsx),
  // so the call keeps going while the shell switches views underneath it.
  const openToolRef = useRef(openTool);
  openToolRef.current = openTool;
  const goHomeRef = useRef(goHome);
  goHomeRef.current = goHome;
  useEffect(
    () =>
      onOpenScreen((target) => {
        if (target.kind === 'tool') openToolRef.current(target.toolId);
        else if (target.kind === 'settings') setSettingsOpen(true);
        else goHomeRef.current(); // home, or the Pantheon (V10Home pans there itself)
      }),
    [],
  );

  const modals = (
    <>
      <InfoPopup
        open={overlay === 'info'}
        onClose={() => setOverlay('none')}
        gear={activeGear}
        toolId={view === 'tool' ? toolId : null}
        onOpenChat={() => setOverlay('chat')}
      />
      <SyqiChat open={overlay === 'chat'} onClose={() => setOverlay('none')} />
      <HrmezPopup
        open={overlay === 'hrmez'}
        onClose={() => setOverlay('none')}
        onOpenTool={(id) => {
          setOverlay('none');
          openTool(id);
        }}
      />
      <NavDrawer
        open={drawerOpen}
        userId={userId}
        onClose={() => setDrawerOpen(false)}
        onOpenTool={(id) => openTool(id)}
        onOpenHome={goHome}
        // Settings opens only after the drawer Modal has fully unmounted
        // (onClosed), so two RN-web Modal portals never overlap.
        onOpenSettings={() => {
          settingsPending.current = true;
        }}
        onClosed={() => {
          if (settingsPending.current) {
            settingsPending.current = false;
            setSettingsOpen(true);
          }
        }}
      />
      <SettingsSheet
        open={settingsOpen}
        onClose={() => {
          setSettingsOpen(false);
          setSettingsRev((n) => n + 1);
        }}
        userId={userId}
      />
    </>
  );

  // One header everywhere (portrait shell + landscape tool): menu, back, title,
  // Home + the user's quick links (Joel, 2026-10-18).
  const header = (
    <OlmpsHeader
      title={title}
      onBack={closeToToday}
      onOpenDrawer={() => setDrawerOpen(true)}
      onHome={goHome}
      onTalk={() => toggleTalk('syqi', 'SyQi')}
      onCapture={() => setOverlay('hrmez')}
      onOpenTool={(id) => openTool(id)}
      onOpenSettings={() => setSettingsOpen(true)}
    />
  );

  // ---- Landscape + tool open: full-screen pinch-zoomable tool under the
  // same header as portrait (the old clock-only chrome hid the quick links). ----
  if (isLandscape && view === 'tool' && toolId) {
    return (
      <SafeAreaView style={styles.rootLandscape} edges={['top', 'bottom', 'left', 'right']}>
        <Atmosphere />
        {header}
        <GestureDetector gesture={pinch}>
          <View style={styles.landscapeStage}>
            <View style={[styles.landscapeTool, { transform: [{ scale: zoom }] }]}>
              <ToolPanel
                sector={SHELL_SECTOR}
                stage={SHELL_STAGE}
                gear={activeGear}
                toolIds={[toolId]}
                activeToolIndex={0}
                onSelectToolIndex={() => {}}
                onClose={closeToToday}
                onOpenInfo={() => setOverlay('info')}
                toolProps={toolProps}
                onOpenTool={(id) => openTool(id)}
              />
            </View>
          </View>
        </GestureDetector>

        {zoom !== 1 && (
          <Pressable style={styles.zoomBadge} onPress={resetZoom}>
            <GlassFill strong radius={999} />
            <Text style={styles.zoomBadgeText}>{Math.round(zoom * 100)}% · reset</Text>
          </Pressable>
        )}

        {landOverlay === 'order' && (
          <View style={styles.orderFlyout}>
            {GEARS.map((gear) => (
              <GearTile
                key={gear}
                letter={GEAR_META[gear].letter}
                active={gear === activeGear}
                onPress={() => routeGear(gear)}
              />
            ))}
          </View>
        )}

        {modals}
      </SafeAreaView>
    );
  }

  // ---- v10 home: LIFE ← NOW → SELF, full-bleed with its own chrome. ----
  if (view === 'now') {
    return (
      <TodayDataProvider value={today}>
        <V10Home
          userId={userId}
          today={today}
          onOpenTool={(id, gear, opts) => openTool(id, gear, opts)}
          onOpenDrawer={() => setDrawerOpen(true)}
          onCapture={() => setOverlay('hrmez')}
          onOpenSettings={() => setSettingsOpen(true)}
          onOpenAstra={openAstra}
          onOpenSession={openSession}
          homeTick={homeTick}
        />
        {modals}
        {revealOpen && <SpectrumReveal userId={userId} onClose={() => setRevealOpen(false)} />}
      </TodayDataProvider>
    );
  }

  // ---- Portrait (and any other landscape/view combo): the normal shell. ----
  return (
    <TodayDataProvider value={today}>
    <SafeAreaView style={styles.root} edges={['top', 'bottom']}>
      {/* Heavenly backdrop — layered soft cloud blooms, sitting behind the
          whole shell. Purely atmospheric; the neu shell panel above it is
          fully opaque so legibility is unaffected. */}
      <CloudBackdrop />
      <View style={styles.card}>
        <GlassFill radius={radii.shell} />
        {header}
        <NowStrip onOpenStep={openStep} />
        <View style={styles.content} nativeID="olmps-shell-content">
          {view === 'oracle' && (
            <SyqiHome
              userId={userId}
              onOpenCompass={openAstra}
              onOpenTool={(id, gear, opts) => openTool(id, gear, opts)}
              onOpenStep={openStep}
              onOpenSession={openSession}
              onOpenSpine={openSpine}
            />
          )}
          {view === 'astra' && (
            <CompassView key={settingsRev} userId={userId} onOpenTool={(id) => openTool(id)} />
          )}
          {view === 'spectrum' && <SpectrumScreen userId={userId} onOpenTool={(id) => openTool(id)} />}
          {view === 'tool' && toolId && (
            <ToolPanel
              sector={SHELL_SECTOR}
              stage={SHELL_STAGE}
              gear={activeGear}
              toolIds={[toolId]}
              activeToolIndex={0}
              onSelectToolIndex={() => {}}
              onClose={closeToToday}
              onOpenInfo={() => setOverlay('info')}
              toolProps={toolProps}
              onOpenTool={(id, opts) => openTool(id, undefined, opts)}
            />
          )}
          {view === 'step' && stepItem && (
            <StepScreen
              item={stepItem}
              onClose={closeToToday}
              onOpenTool={(id, opts) => openTool(id, undefined, opts)}
            />
          )}
          {view === 'session' && (
            <SessionFlow
              kind={sessionKind}
              onDone={closeToToday}
              onOpenTool={(id) => openTool(id)}
              game={compassConfig.game}
              ledger={compassConfig.ledger}
              markDay={markDay}
            />
          )}
          {view === 'spine' && (
            <SpineFlow userId={userId} onExit={closeToToday} onOpenTool={(id) => openTool(id)} />
          )}
        </View>
      </View>

      {modals}

      {revealOpen && <SpectrumReveal userId={userId} onClose={() => setRevealOpen(false)} />}
    </SafeAreaView>
    </TodayDataProvider>
  );
}

const useStyles = makeThemedStyles(({ t, c, neu }) =>
  StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: t.night,
  },
  rootLandscape: {
    flex: 1,
    backgroundColor: t.night,
  },
  landscapeStage: {
    flex: 1,
    overflow: 'hidden',
  },
  landscapeTool: {
    flex: 1,
    margin: 6,
  },
  floatButton: {
    position: 'absolute',
    top: 10,
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'transparent',
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 20,
    ...neu.raisedSm,
  },
  floatLeft: { left: 12 },
  floatRight: { right: 12 },
  zoomBadge: {
    position: 'absolute',
    bottom: 12,
    alignSelf: 'center',
    backgroundColor: 'transparent',
    overflow: 'hidden',
    borderRadius: radii.pill,
    paddingHorizontal: 14,
    paddingVertical: 6,
    zIndex: 20,
    ...neu.raisedSm,
  },
  zoomBadgeText: { fontWeight: '800', fontSize: 12, color: t.goldBright },
  orderFlyout: {
    position: 'absolute',
    top: 60,
    left: 12,
    flexDirection: 'row',
    gap: 10,
    backgroundColor: t.glass,
    borderRadius: radii.nav,
    padding: 10,
    zIndex: 30,
    ...neu.raised,
  },
  card: {
    flex: 1,
    margin: 10,
    borderRadius: radii.shell,
    // L1 of the tonal ladder — the shell sheet must be darker than the
    // panels (c.card) that sit on it, lighter than the outer chrome.
    // Reverted to fully opaque (2026-07-26): translucent here let the
    // CloudBackdrop (and anything else behind it) bleed through every
    // screen's content — including tool screens like CLIO whose cards
    // assume a solid backdrop. Cloud visibility needs a narrower approach
    // that doesn't touch this shared shell sheet.
    backgroundColor: 'transparent',
    overflow: 'hidden',
    ...neu.raisedLg,
  },
  content: { flex: 1 },
  }),
);
